import SockJS from "sockjs-client";
import Stomp from "stompjs";
import * as Constants from "./Constants";

/**
 * Shared, app-wide realtime store for Track Point (MOBI_TRACK) live locations — the web equivalent
 * of the Android DeviceRealtimeBus. A single STOMP subscription to `/topic/gpsData` is fanned out to
 * every subscriber, keyed by the driver's phone (== the asset unit id), so **no component polls
 * `mobi-track/latest` on a timer anymore**.
 *
 * Scope guard: only MOBI_TRACK frames are handled here; GT06/FT920 frames on the same topic are
 * ignored, so tracker/lock behaviour is untouched.
 *
 * The socket is the source of truth for both position AND presence: `subscribe` delivers each frame,
 * and `isOnline(phone)` reports true while a frame has been seen within ONLINE_WINDOW_MS. A one-time
 * REST seed (done by the caller) covers "was the driver already online before this page opened".
 */

const ONLINE_WINDOW_MS = 3 * 60 * 1000; // matches the platform's MobiTrack online window

const listeners = new Map(); // phone -> Set<cb>
// phone -> { latitude, longitude, timestamp, receivedMs, eventMs, offline }
//   receivedMs = wall-clock time a live socket frame landed (null for a REST seed)
//   eventMs    = parsed frame/seed timestamp (the driver's post time)
const latest = new Map();
let stompClient = null;
let refCount = 0;

function parseMs(ts) {
  if (!ts) return NaN;
  const ms = new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

function handleFrame(body) {
  let data;
  try {
    data = JSON.parse(body);
  } catch (e) {
    return;
  }
  if (!data || data.assetType !== "MOBI_TRACK" || !data.deviceId) return;
  const phone = String(data.deviceId);
  const isOffline = data.status === "OFFLINE";
  const prev = latest.get(phone);
  const incomingEventMs = parseMs(data.timestamp);
  // Monotonic guard: never let a frame older than the state we've already applied overwrite it.
  // STOMP gives no ordering or de-dup guarantee, and frames can be replayed (LocationSyncWorker
  // flushing buffered points) or re-emitted out of order on reconnect. Without this, a stale
  // location frame flips a correctly-offline driver back green, and a late duty-off frame knocks a
  // driver who already resumed offline. Frames with no parseable timestamp are applied best-effort
  // (they can't be ordered). This mirrors the Android map's high-water latch.
  if (
    prev &&
    Number.isFinite(incomingEventMs) &&
    Number.isFinite(prev.eventMs) &&
    incomingEventMs < prev.eventMs
  ) {
    return;
  }
  const frame = isOffline
    ? {
        // Duty-off signal: keep the last known position so the marker doesn't jump,
        // but flag it offline so isMobiTrackLive() reports false immediately.
        latitude: data.latitude != null ? data.latitude : prev && prev.latitude,
        longitude: data.longitude != null ? data.longitude : prev && prev.longitude,
        timestamp: data.timestamp,
        receivedMs: Date.now(),
        eventMs: parseMs(data.timestamp),
        offline: true,
      }
    : {
        latitude: data.latitude,
        longitude: data.longitude,
        timestamp: data.timestamp,
        receivedMs: Date.now(),
        eventMs: parseMs(data.timestamp),
        offline: false,
      };
  latest.set(phone, frame);
  const cbs = listeners.get(phone);
  if (cbs) {
    cbs.forEach((cb) => {
      try {
        cb(frame);
      } catch (e) {
        /* a bad subscriber must not break fan-out */
      }
    });
  }
}

const reconnectListeners = new Set(); // fired after the socket RE-connects (not first connect)
let reconnectTimer = null;
let hasConnected = false;

/**
 * Register a callback fired whenever the socket comes back after a drop. STOMP topics do NOT
 * replay messages missed while disconnected, so any "driver went online/offline" frame broadcast
 * during the outage (e.g. the manager was in a lift) is lost — the callback is the signal to
 * re-seed presence from REST at once instead of waiting for the driver's next post. Returns an
 * unsubscribe function.
 */
export function onSocketReconnect(cb) {
  if (typeof cb !== "function") return () => {};
  reconnectListeners.add(cb);
  return () => reconnectListeners.delete(cb);
}

function notifyReconnect() {
  reconnectListeners.forEach((cb) => {
    try {
      cb();
    } catch (e) {
      /* a bad listener must not break the others */
    }
  });
}

function scheduleReconnect() {
  if (refCount <= 0 || reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (refCount > 0) connect();
  }, 3000);
}

function connect() {
  try {
    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    stompClient = Stomp.over(socket);
    stompClient.debug = null;
    stompClient.connect(
      {},
      () => {
        stompClient.subscribe("/topic/gpsData", (message) => handleFrame(message.body));
        // On a RE-connect, frames missed during the outage are gone (STOMP has no replay). Ask the
        // server — over the SAME socket, no HTTP — to re-emit the current state for every phone we
        // care about; those frames flow back through handleFrame like any live update. Also notify
        // listeners so any surface that needs to react can.
        if (hasConnected) {
          requestMobiTrackStatus(Array.from(listeners.keys()));
          notifyReconnect();
        }
        hasConnected = true;
      },
      () => {
        // connect/transport error — retry while anything still cares, then re-seed on success.
        scheduleReconnect();
      }
    );
    // SockJS drops (network flip, lift) close the socket without an error callback.
    if (socket) socket.onclose = () => scheduleReconnect();
  } catch (e) {
    stompClient = null;
    scheduleReconnect();
  }
}

function disconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  hasConnected = false;
  try {
    if (stompClient && stompClient.connected) stompClient.disconnect();
  } catch (e) {
    /* ignore */
  }
  stompClient = null;
}

/**
 * Subscribe to a driver's live frames. Returns an unsubscribe function. The most recent cached frame
 * (if any) is delivered synchronously so a newly-opened screen paints immediately.
 */
export function subscribeMobiTrack(phone, cb) {
  if (!phone || typeof cb !== "function") return () => {};
  const key = String(phone);
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(cb);
  refCount += 1;
  if (refCount === 1) connect();

  const cached = latest.get(key);
  if (cached) {
    try {
      cb(cached);
    } catch (e) {
      /* ignore */
    }
  }

  return function unsubscribe() {
    const set = listeners.get(key);
    if (set) {
      set.delete(cb);
      if (set.size === 0) listeners.delete(key);
    }
    refCount = Math.max(0, refCount - 1);
    if (refCount === 0) disconnect();
  };
}

/** Latest cached frame for a phone, or null. */
export function getLatestMobiTrack(phone) {
  return latest.get(String(phone)) || null;
}

/**
 * Seed presence from a one-shot REST `latest` fetch (initial paint / re-fetch on select). Records
 * the driver's post time as `eventMs` with `receivedMs = null` so it counts toward the online
 * window from the *post* time. A live socket frame always wins — seeding never clobbers a frame
 * we've actually received (that would make a genuinely-live driver look stale), and an explicit
 * duty-off flag is preserved. This is the single presence authority shared by the list and the
 * detail panel, so they can no longer disagree.
 */
export function seedMobiTrack(phone, data, force = false) {
  if (!phone || !data) return;
  const key = String(phone);
  const prev = latest.get(key);
  // Normally a live socket frame wins (don't let a stale seed downgrade a genuinely-live driver).
  // But after a reconnect the cached frame is from BEFORE the outage and can't be trusted, so
  // `force` lets the fresh REST truth override it (this is how the "manager in a lift" case
  // corrects the instant the socket is back).
  if (!force && prev && prev.receivedMs != null) return;
  latest.set(key, {
    latitude: data.latitude,
    longitude: data.longitude,
    timestamp: data.timestamp,
    receivedMs: null,
    eventMs: parseMs(data.timestamp),
    // Prefer the server's authoritative `online` verdict (on duty AND fresh, computed with the one
    // shared window) so the client renders presence verbatim instead of re-deriving it. `online:false`
    // means off-duty OR stale — either way, offline. Fall back to the durable off-duty `offline`
    // marker when talking to a backend that doesn't send `online` yet (then the local window decides).
    offline: data.offline === true || data.online === false,
  });
}

/** Clear all cached presence (call on logout / user switch so no state leaks across sessions). */
export function resetMobiTrackStore() {
  latest.clear();
}

/**
 * Ask the server (over the socket, no HTTP) to re-emit the current presence for these phones on
 * `/topic/gpsData`. Used on reconnect to catch up on state that changed while the socket was down.
 * A no-op if the socket isn't currently connected.
 */
export function requestMobiTrackStatus(phones) {
  if (!stompClient || !stompClient.connected) return;
  const list = (phones && phones.length ? phones : Array.from(listeners.keys())).map(String);
  if (list.length === 0) return;
  try {
    stompClient.send("/app/mobi-track/status-request", {}, JSON.stringify({phoneNumbers: list}));
  } catch (e) {
    /* ignore — the reconnect path or next frame will recover */
  }
}

/**
 * True when the driver is currently online, within the online window. `now` is injectable for
 * tests. The basis is the socket receive time when a live frame exists, else the seeded post time
 * — so presence is fresh whether it came over the socket or a REST seed, and goes false once the
 * window lapses with no new data (fixes "stuck ONLINE" for REST-only rows).
 */
export function isMobiTrackLive(phone, now = Date.now()) {
  const f = latest.get(String(phone));
  if (!f || f.offline) return false;
  const basis = f.receivedMs != null ? f.receivedMs : f.eventMs;
  return Number.isFinite(basis) && now - basis < ONLINE_WINDOW_MS;
}

/** Test-only: seed a frame as if it arrived over the socket. */
export function _seedFrameForTest(phone, frame) {
  latest.set(String(phone), { receivedMs: Date.now(), ...frame });
}

/** Test-only: drive a raw `/topic/gpsData` payload through the real frame handler (incl. the
 *  monotonic ordering guard and fan-out), exactly as the socket subscription would. */
export function _handleRawFrameForTest(body) {
  handleFrame(body);
}

export { ONLINE_WINDOW_MS };
